import type { TaskRowVM } from "../components/TaskRow";
import type { CreateTaskInput, UpdateTaskInput } from "../api/tm";
import { PRIO, STATUS, type Priority, type Status } from "./design";
import { dueDateToIso, todayKey } from "./date";

/** Moteur de vues sauvegardées — fonctions pures sur TaskRowVM.
 * ponytail: persistance localStorage (uiStore) ; SavedView est sérialisable
 * tel quel vers un futur endpoint TM de config utilisateur. */

export type ViewLayout = "list" | "board" | "calendar" | "gallery" | "timeline";
export type GroupField =
  "none" | "status" | "priority" | "list" | "assignee" | "due";
export type SortField = "title" | "priority" | "due" | "status";
export interface SortRule {
  field: SortField;
  dir: "asc" | "desc";
}

export type FilterField =
  "title" | "status" | "priority" | "list" | "tag" | "due" | "assignee";
export type FilterOp =
  | "contains"
  | "is"
  | "is_not"
  | "empty"
  | "not_empty"
  | "overdue"
  | "this_week"
  | "before"
  | "after";
export interface FilterCondition {
  field: FilterField;
  op: FilterOp;
  value?: string;
}
/** Filtre plat v1 : une seule couche de conditions reliées par et/ou. */
export interface FlatFilter {
  operator: "and" | "or";
  conditions: FilterCondition[];
}

/** Propriétés de ligne masquables (panneau « Propriétés »). */
export type RowProp =
  | "status"
  | "priority"
  | "due"
  | "list"
  | "project"
  | "assignees"
  | "tags"
  | "subtasks";

export interface SavedView {
  id: string;
  name: string;
  layout: ViewLayout;
  groupBy: GroupField;
  sort: SortRule[];
  filter: FlatFilter;
  hidden: RowProp[];
  /** Ordre des colonnes du layout Board (clés de groupe) — front-only. */
  columnOrder?: string[];
}

export const FILTER_FIELDS: Record<
  FilterField,
  { label: string; type: "text" | "enum" | "date" }
> = {
  title: { label: "Titre", type: "text" },
  status: { label: "Statut", type: "enum" },
  priority: { label: "Priorité", type: "enum" },
  list: { label: "Liste", type: "enum" },
  tag: { label: "Tag", type: "text" },
  due: { label: "Échéance", type: "date" },
  assignee: { label: "Assigné", type: "text" },
};

export const OPS_BY_TYPE: Record<"text" | "enum" | "date", FilterOp[]> = {
  text: ["contains", "is", "empty", "not_empty"],
  enum: ["is", "is_not", "empty"],
  date: ["overdue", "this_week", "is", "before", "after", "empty"],
};

export const OP_LABELS: Record<FilterOp, string> = {
  contains: "contient",
  is: "est",
  is_not: "n'est pas",
  empty: "est vide",
  not_empty: "n'est pas vide",
  overdue: "en retard",
  this_week: "cette semaine",
  before: "avant",
  after: "après",
};

function fieldValue(vm: TaskRowVM, field: FilterField): string | undefined {
  switch (field) {
    case "title":
      return vm.title;
    case "status":
      return vm.status;
    case "priority":
      return vm.priority;
    case "list":
      return vm.listName;
    case "tag":
      return vm.tags.join(" ");
    case "due":
      return vm.dueKey;
    case "assignee":
      return vm.assignees?.map((a) => a.name).join(" ") ?? vm.driverName;
  }
}

function endOfWeekKey(today: string): string {
  const [y, m, d] = today.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d, 12));
  const dow = date.getUTCDay() || 7; // lundi=1 … dimanche=7
  date.setUTCDate(date.getUTCDate() + (7 - dow));
  return date.toISOString().slice(0, 10);
}

export function matchCondition(
  vm: TaskRowVM,
  c: FilterCondition,
  today = todayKey(),
): boolean {
  const raw = fieldValue(vm, c.field);
  const needle = (c.value ?? "").toLowerCase();
  switch (c.op) {
    case "contains":
      return !!raw && raw.toLowerCase().includes(needle);
    case "is":
      if (c.field === "due") return raw === c.value;
      return !!raw && raw.toLowerCase() === needle;
    case "is_not":
      return !raw || raw.toLowerCase() !== needle;
    case "empty":
      return !raw;
    case "not_empty":
      return !!raw;
    case "overdue":
      return !!vm.dueKey && vm.dueKey < today && vm.status !== "done";
    case "this_week":
      return (
        !!vm.dueKey && vm.dueKey >= today && vm.dueKey <= endOfWeekKey(today)
      );
    case "before":
      return !!vm.dueKey && !!c.value && vm.dueKey < c.value;
    case "after":
      return !!vm.dueKey && !!c.value && vm.dueKey > c.value;
  }
}

export function applyFilter(
  vms: TaskRowVM[],
  filter: FlatFilter,
  today = todayKey(),
): TaskRowVM[] {
  if (!filter.conditions.length) return vms;
  return vms.filter((vm) => {
    const results = filter.conditions.map((c) => matchCondition(vm, c, today));
    return filter.operator === "and"
      ? results.every(Boolean)
      : results.some(Boolean);
  });
}

const PRIO_ORDER: Record<string, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};
const STATUS_ORDER: Record<string, number> = {
  todo: 0,
  in_progress: 1,
  blocked: 2,
  done: 3,
  cancelled: 4,
  archived: 5,
};

function compareBy(a: TaskRowVM, b: TaskRowVM, rule: SortRule): number {
  let cmp = 0;
  switch (rule.field) {
    case "title":
      cmp = a.title.localeCompare(b.title, "fr");
      break;
    case "priority":
      cmp = (PRIO_ORDER[a.priority] ?? 9) - (PRIO_ORDER[b.priority] ?? 9);
      break;
    case "due":
      cmp = (a.dueKey ?? "9999").localeCompare(b.dueKey ?? "9999");
      break;
    case "status":
      cmp = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
      break;
  }
  return rule.dir === "desc" ? -cmp : cmp;
}

/** Tri multi-critères + stabilisation Titre A→Z (comme le prototype). */
export function applySort(vms: TaskRowVM[], rules: SortRule[]): TaskRowVM[] {
  return [...vms].sort((a, b) => {
    for (const rule of rules) {
      const cmp = compareBy(a, b, rule);
      if (cmp !== 0) return cmp;
    }
    return a.title.localeCompare(b.title, "fr");
  });
}

export interface TaskGroup {
  key: string;
  label: string;
  /** Pastille du groupe (couleur statut/priorité) le cas échéant. */
  color?: string;
  items: TaskRowVM[];
}

export function applyGroup(vms: TaskRowVM[], groupBy: GroupField): TaskGroup[] {
  if (groupBy === "none")
    return [{ key: "all", label: "Toutes les tâches", items: vms }];

  const groups = new Map<string, TaskGroup>();
  const push = (key: string, label: string, vm: TaskRowVM, color?: string) => {
    let g = groups.get(key);
    if (!g) {
      g = { key, label, color, items: [] };
      groups.set(key, g);
    }
    g.items.push(vm);
  };

  for (const vm of vms) {
    switch (groupBy) {
      case "status":
        push(vm.status, STATUS[vm.status].label, vm, STATUS[vm.status].dot);
        break;
      case "priority":
        push(vm.priority, PRIO[vm.priority].label, vm, PRIO[vm.priority].color);
        break;
      case "list":
        push(vm.listName ?? "∅", vm.listName ?? "Sans liste", vm);
        break;
      case "assignee": {
        const names = vm.assignees?.length
          ? vm.assignees.map((a) => a.name)
          : [vm.driverName ?? "Non assignée"];
        for (const n of names) push(n, n, vm);
        break;
      }
      case "due":
        push(vm.dueKey ?? "∅", vm.dueKey ?? "Sans échéance", vm);
        break;
    }
  }

  const ordered = [...groups.values()];
  if (groupBy === "status")
    ordered.sort(
      (a, b) => (STATUS_ORDER[a.key] ?? 9) - (STATUS_ORDER[b.key] ?? 9),
    );
  else if (groupBy === "priority")
    ordered.sort((a, b) => (PRIO_ORDER[a.key] ?? 9) - (PRIO_ORDER[b.key] ?? 9));
  else ordered.sort((a, b) => a.key.localeCompare(b.key, "fr"));
  return ordered;
}

/** Pipeline complet d'une vue : filtre → tri (le groupement reste au layout). */
export function applyView(
  vms: TaskRowVM[],
  view: SavedView,
  today = todayKey(),
): TaskRowVM[] {
  return applySort(applyFilter(vms, view.filter, today), view.sort);
}

/** Phrase récapitulative du filtre — « Statut est En cours et Échéance en retard ». */
export function filterSentence(filter: FlatFilter): string {
  if (!filter.conditions.length) return "Aucun filtre";
  const joiner = filter.operator === "and" ? " et " : " ou ";
  return filter.conditions
    .map((c) => {
      const f = FILTER_FIELDS[c.field].label;
      const op = OP_LABELS[c.op];
      const v =
        c.value &&
        !["empty", "not_empty", "overdue", "this_week"].includes(c.op)
          ? ` ${enumLabel(c.field, c.value)}`
          : "";
      return `${f} ${op}${v}`;
    })
    .join(joiner);
}

function enumLabel(field: FilterField, value: string): string {
  if (field === "status" && value in STATUS)
    return STATUS[value as keyof typeof STATUS].label;
  if (field === "priority" && value in PRIO)
    return PRIO[value as keyof typeof PRIO].label;
  return value;
}

/** Valeurs proposées pour un champ enum du constructeur de filtres. */
export function enumOptions(
  field: FilterField,
  listNames: string[],
): { value: string; label: string }[] {
  if (field === "status")
    return Object.entries(STATUS).map(([k, v]) => ({
      value: k,
      label: v.label,
    }));
  if (field === "priority")
    return Object.entries(PRIO).map(([k, v]) => ({ value: k, label: v.label }));
  if (field === "list") return listNames.map((n) => ({ value: n, label: n }));
  return [];
}

/** Patch de mutation quand on dépose une tâche dans un groupe (D&D).
 * null = groupe non mutable : liste (UpdateTaskInput sans list_id),
 * assigné (API assignments dédiée), groupes dérivés (en retard…). */
export function groupPatch(
  groupBy: GroupField,
  key: string,
): UpdateTaskInput | null {
  switch (groupBy) {
    case "status":
      return key in STATUS ? { status: key as Status } : null;
    case "priority":
      return key in PRIO ? { priority: key as Priority } : null;
    case "due":
      return key === "∅" ? { due_date: null } : { due_date: dueDateToIso(key) };
    default:
      return null;
  }
}

/** Pré-remplissage du composer inline d'un groupe. null = composer masqué.
 * `status` est appliqué en PATCH post-création (CreateTaskInput sans status). */
export function groupCreateDefaults(
  groupBy: GroupField,
  key: string,
  lists: { id: string; name: string }[],
): { create?: Partial<CreateTaskInput>; status?: Status } | null {
  switch (groupBy) {
    case "none":
      return {};
    case "status":
      return key in STATUS ? { status: key as Status } : null;
    case "priority":
      return key in PRIO ? { create: { priority: key as Priority } } : null;
    case "list": {
      if (key === "∅") return {};
      const l = lists.find((x) => x.name === key);
      return l ? { create: { list_id: l.id } } : null;
    }
    case "due":
      return key === "∅" ? {} : { create: { due_date: dueDateToIso(key) } };
    case "assignee":
      // ponytail: création + assignment = 2 appels chaînés — composer masqué,
      // à câbler si le besoin apparaît.
      return null;
  }
}

let seq = 0;
export function newViewId(): string {
  return `v${Date.now().toString(36)}${(seq++).toString(36)}`;
}

/** Vues seed du prototype Todo DECO. */
export function seedViews(): SavedView[] {
  const f = (conditions: FilterCondition[] = []): FlatFilter => ({
    operator: "and",
    conditions,
  });
  return [
    {
      id: "seed-all",
      name: "Toutes",
      layout: "list",
      groupBy: "none",
      sort: [{ field: "due", dir: "asc" }],
      filter: f(),
      hidden: [],
    },
    {
      id: "seed-open",
      name: "À traiter",
      layout: "list",
      groupBy: "priority",
      sort: [{ field: "due", dir: "asc" }],
      filter: f([
        { field: "status", op: "is_not", value: "done" },
        { field: "status", op: "is_not", value: "cancelled" },
      ]),
      hidden: [],
    },
    {
      id: "seed-overdue",
      name: "En retard",
      layout: "list",
      groupBy: "none",
      sort: [{ field: "due", dir: "asc" }],
      filter: f([{ field: "due", op: "overdue" }]),
      hidden: [],
    },
    {
      id: "seed-week",
      name: "À compléter",
      layout: "list",
      groupBy: "due",
      sort: [{ field: "priority", dir: "asc" }],
      filter: f([{ field: "due", op: "this_week" }]),
      hidden: [],
    },
    {
      id: "seed-board",
      name: "Board statut",
      layout: "board",
      groupBy: "status",
      sort: [{ field: "priority", dir: "asc" }],
      filter: f(),
      hidden: [],
    },
    {
      id: "seed-cal",
      name: "Calendrier",
      layout: "calendar",
      groupBy: "none",
      sort: [{ field: "due", dir: "asc" }],
      filter: f(),
      hidden: [],
    },
  ];
}
